import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps, SwitchProps } from 'antd'
import { SwitchChangeEventHandler } from 'antd/lib/switch'
import { TooltipPlacement } from 'antd/lib/tooltip'
import cl from 'classnames'

import { Icon, InlineTag, Switch } from 'Shared/atoms'
import { IconType } from 'Shared/atoms/Icon/Icon'
import { withTooltip } from 'Shared/HOCs/withTooltip'
import { hasError } from 'Shared/utils/validation'

import { FieldNameRegexType } from 'Core/interfaces'

import FormItem from './FormItem'

import styles from './switch.module.scss'

type FormSwitchProps<FieldsType extends FieldValues> = SwitchProps &
	FormItemProps & {
		label?: string
		wrapperClassName?: string
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		disabled?: boolean
		isLabelColon?: boolean
		isWhiteSpace?: boolean
		isNextToLabel?: boolean
		showTooltip?: boolean
		tooltipText?: string
		tooltipPlacement?: TooltipPlacement
		isAdditionalIcon?: boolean
		iconTooltipText?: string
		iconShowTooltip?: boolean
		iconTooltipPlacement?: TooltipPlacement
		iconType?: IconType
		validate?: boolean
		readOnly?: boolean
	}

const FormItemWithTooltip = withTooltip(FormItem)
const IconWithTooltip = withTooltip(Icon)

function FormSwitch<FieldsType extends FieldValues>({
	label,
	wrapperClassName,
	control,
	fieldName,
	disabled,
	isWhiteSpace = false,
	isLabelColon = true,
	showTooltip = false,
	isAdditionalIcon = false,
	isNextToLabel = false,
	tooltipText,
	tooltipPlacement,
	iconTooltipText,
	iconShowTooltip,
	iconTooltipPlacement,
	iconType,
	validate,
	readOnly = false,
	...props
}: FormSwitchProps<FieldsType>) {
	const correctLabel =
		isLabelColon && label ? `${label}:` : label && !isLabelColon ? label : undefined

	return (
		<Controller
			name={fieldName as FieldPath<FieldsType>}
			control={control}
			render={({
				field: { value, name, onChange, ...fieldProps },
				fieldState: { error }
			}) => {
				const handleChange: SwitchChangeEventHandler | undefined = (...args) => {
					props.onChange?.(...args) || onChange(...args)
				}

				return (
					<FormItemWithTooltip
						className={cl(styles.switchWrapper, wrapperClassName, {
							[styles.disabled]: disabled,
							[styles.whiteSpace]: isWhiteSpace,
							[styles.additionalIcon]: isAdditionalIcon,
							[styles.nextToLabel]: isNextToLabel
						})}
						label={correctLabel}
						name={name}
						value={value}
						error={hasError(error)}
						colon={false}
						showTooltip={showTooltip}
						tooltipText={tooltipText}
						tooltipPlacement={tooltipPlacement}
						extra={props.extra}
						validate={validate}
					>
						{isAdditionalIcon && iconType && (
							<IconWithTooltip
								type={iconType}
								tooltipText={iconTooltipText}
								showTooltip={iconShowTooltip}
								tooltipPlacement={iconTooltipPlacement}
								value={value}
							/>
						)}
						{readOnly ? (
							<InlineTag size="s" type={value ? 'green' : 'disabled'}>
								{value ? 'YES' : 'NO'}
							</InlineTag>
						) : (
							<Switch
								{...props}
								{...fieldProps}
								disabled={disabled}
								checked={!!value}
								onChange={handleChange}
							/>
						)}
					</FormItemWithTooltip>
				)
			}}
		/>
	)
}

export default FormSwitch
